import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User, Share2, Facebook, Copy, Check, BookOpen } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { toast } from "sonner";
import api from "../lib/api";
import Seo from "../components/Seo";

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/posts/${slug}`);
        if (!mounted) return;
        setPost(data);
        try {
          const { data: list } = await api.get(`/posts?category=${data.category}`);
          if (!mounted) return;
          setRelated(list.filter((p) => p.id !== data.id).slice(0, 3));
        } catch (e) { /* silent */ }
      } catch (err) {
        if (err.response?.status === 404) {
          navigate("/blog", { replace: true });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    window.scrollTo({ top: 0 });
    return () => { mounted = false; };
  }, [slug, navigate]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <main id="main-content" className="bg-[#050505] min-h-screen flex items-center justify-center" data-testid="blog-post-loading">
        <div className="w-10 h-10 border-2 border-electric border-t-transparent rounded-full animate-spin" role="status" aria-label="Loading post" />
      </main>
    );
  }

  if (!post) return null;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="blog-post-page">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover_image || undefined}
        type="article"
      />

      <article className="max-w-3xl mx-auto px-5 lg:px-10 pt-16 pb-24">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-electric text-sm font-bold uppercase tracking-wider mb-8 focus-visible:ring-2 focus-visible:ring-electric/40 rounded-md px-1"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> All Posts
        </Link>

        <div className="flex items-center gap-3 text-xs uppercase tracking-wider mb-6">
          <span className="px-3 py-1 rounded-full bg-electric/10 text-electric font-bold border border-electric/20">
            {post.category}
          </span>
          <span className="text-zinc-500 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" aria-hidden="true" />{fmt(post.published_at)}
          </span>
          <span className="text-zinc-500 flex items-center gap-1.5">
            <User className="w-3 h-3" aria-hidden="true" />{post.author}
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6"
        >
          {post.title}
        </motion.h1>

        <p className="text-xl text-zinc-300 leading-relaxed mb-10">{post.excerpt}</p>

        {post.cover_image && (
          <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-12 border border-white/5">
            <img src={post.cover_image} alt="" className="w-full h-full object-cover" />
          </div>
        )}

        <div
          className="prose prose-invert prose-electric max-w-none text-zinc-200 leading-[1.8] text-lg
            prose-headings:font-display prose-headings:font-black prose-headings:tracking-tight prose-headings:text-white
            prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
            prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:my-5
            prose-a:text-electric prose-a:no-underline hover:prose-a:underline
            prose-strong:text-white
            prose-blockquote:border-l-electric prose-blockquote:bg-electric/5 prose-blockquote:py-1 prose-blockquote:px-5 prose-blockquote:not-italic prose-blockquote:text-zinc-200 prose-blockquote:rounded-r-lg
            prose-code:text-electric prose-code:bg-electric/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:hidden prose-code:after:hidden
            prose-img:rounded-xl prose-img:my-8
            prose-ul:my-5 prose-li:my-1
            prose-hr:border-white/10 prose-hr:my-10"
          data-testid="blog-post-content"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{post.content}</ReactMarkdown>
        </div>

        {post.tags?.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wider px-3 py-1 rounded-full bg-white/5 text-zinc-300 border border-white/10">
                #{t}
              </span>
            ))}
          </div>
        )}

        {/* SHARE */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-wrap items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold flex items-center gap-2">
            <Share2 className="w-3.5 h-3.5 text-electric" aria-hidden="true" /> Share
          </div>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
            data-testid="share-fb"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-electric hover:border-electric text-sm transition focus-visible:ring-2 focus-visible:ring-electric/40"
          >
            <Facebook className="w-3.5 h-3.5" aria-hidden="true" /> Facebook
          </a>
          <button
            onClick={copyLink}
            aria-label="Copy link"
            data-testid="share-copy"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-electric hover:border-electric text-sm transition focus-visible:ring-2 focus-visible:ring-electric/40"
          >
            {copied ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : <Copy className="w-3.5 h-3.5" aria-hidden="true" />} {copied ? "Copied" : "Copy Link"}
          </button>
        </div>
      </article>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-[#080808] border-t border-white/5 py-20">
          <div className="max-w-7xl mx-auto px-5 lg:px-10">
            <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
              More from this category
            </div>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-10">
              Keep <span className="italic text-electric">reading.</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group block rounded-2xl overflow-hidden bg-[#0a0a0a] border border-white/5 hover:border-electric/50 transition focus-visible:ring-2 focus-visible:ring-electric/40"
                  data-testid={`related-${p.id}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-electric/15 via-[#0a0a0a] to-[#0a0a0a] flex items-center justify-center">
                        <BookOpen className="w-10 h-10 text-electric opacity-30" aria-hidden="true" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{fmt(p.published_at)}</div>
                    <h3 className="font-display font-bold text-lg leading-tight group-hover:text-electric transition-colors">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
